import { Table } from 'antd';
import { type CardProps, Table as TableV5 } from 'antd5';
import styled, { type DefaultTheme, css } from 'styled-components';

import { Alert, Card } from 'components/ui';
import { Theme } from 'utils/color/horizonTheme';
import { FlexContainer, SpacedContainer } from 'utils/layouts/containers';

type TableCardProps = CardProps & {
  clickable?: boolean;
  fillWidth?: boolean;
  disableUserSort?: boolean;
  theme?: DefaultTheme;
};

export const HeaderContainer = styled(FlexContainer)`
  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

export const Control = styled(SpacedContainer)`
  margin-left: 0;
`;

export const TitleContainer = styled(Control)`
  margin-right: 1rem;
`;

export const TableCard = styled(
  ({
    clickable: _clickable,
    fillWidth: _fillWidth,
    disableUserSort: _disableUserSort,
    theme: _theme,
    ...rest
  }: TableCardProps) => <Card {...rest} />
)`
  background: ${({ theme }) => theme.primaryBackgroundColor};
  .ant-table-thead {
    white-space: nowrap;

    > tr > th {
      border-color: ${({ theme }) => theme.borderColor};
    }
  }
  .ant-card-head {
    background: ${({ theme }) => theme.tertiaryBackgroundColor} !important;
    border-width: 1px;
    padding: 0 1em !important;
  }
  .ant-card-head-wrapper {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .ant-card-head-title {
    max-width: 100%;
    padding: 16px 0;
    padding-right: 1em; /* prevents overflow state while loading */
  }
  .ant-card-extra {
    padding: 1rem 0;
  }

  .ant-spin-blur {
    opacity: ${({ theme }) => (theme.themeName === Theme.Dark ? 0.2 : 0.5)};
  }

  .hidden-column {
    display: none !important;
  }

  tr {
    cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  }

  width: ${props => (props.fillWidth ? '100%' : 'auto')};

  ${({ disableUserSort }) =>
    disableUserSort
      ? `.ant-table-column-has-sorters {
    cursor: default !important;
    pointer-events: none;
  }`
      : ''};
`;

export const DeferBanner = styled(Alert)`
  border-radius: 0;
  border-width: 0 0 1px 0;
  animation: slide-down 0.4s ease-out;

  @keyframes slide-down {
    0% {
      opacity: 0;
      margin-top: -40px;
    }

    100% {
      opacity: 1;
      margin-top: 0;
    }
  }
`;

export const DraggableRow = styled.tr<{
  $isDraggable: boolean;
  $transform?: string;
}>`
  &:not(.ant-table-expanded-row) {
    cursor: ${({ $isDraggable }) => ($isDraggable ? 'grab' : 'default')} !important;
    transform: ${({ $transform }) => $transform ?? 'unset'};
  }
`;

export const ArchivedRowStyle = css`
  .ant-table-row.archived > td,
  .ant-table-row.archived:hover > td {
    color: ${({ theme }) => theme.labelColor};
    background-color: ${({ theme }) =>
      theme.themeName === Theme.Dark ? theme.disabledBackground : theme.selectedListItemBackground};
    font-style: italic;
    border-bottom: 1px solid ${({ theme }) => theme.borderAccentColor};
    border-top: 1px solid ${({ theme }) => theme.borderAccentColor};
  }
`;

export const MarkedTableRowStyle = css`
  .ant-table-row.marked-table-row > td,
  .ant-table-row.marked-table-row:hover > td {
    color: ${({ theme }) => theme.labelColor};
    background-color: ${({ theme }) => theme.alternatingTableRowColor};
    border-bottom: 1px solid ${({ theme }) => theme.borderAccentColor};
    border-top: 1px solid ${({ theme }) => theme.borderAccentColor};
  }
`;

// When a row is selected, transition to selected color in 0.5s;
// when deselected, fade selected color out in 1.5s
export const SelectedRowTransitionStyle = css`
  tr.ant-table-row.ant-table-row-selected > td {
    -webkit-transition: background-color 0.5s ease-in;
    -moz-transition: background-color 0.5s ease-in;
    -o-transition: background-color 0.5s ease-in;
    transition: background-color 0.5s ease-in;
  }
  tr.ant-table-row:not(.ant-table-row-selected) > td {
    -webkit-transition: background-color 1.5s ease-in;
    -moz-transition: background-color 1.5s ease-in;
    -o-transition: background-color 1.5s ease-in;
    transition: background-color 1.5s ease-in;
  }
`;

export const StyledTable = styled(Table)`
  ${ArchivedRowStyle}
  ${MarkedTableRowStyle}
  ${SelectedRowTransitionStyle}

  background: ${({ theme }) => theme.secondaryBackgroundColor};

  .ant-table {
    background: ${({ theme }) => theme.secondaryBackgroundColor};
    color: ${({ theme }) => theme.headerColor};

    .ant-table-header {
      border-radius: 0;
    }

    th,
    thead > tr > td.ant-table-row-expand-icon-cell {
      color: ${({ theme }) => theme.labelColor}; !important;

      ${({ theme }) =>
        theme.themeName === Theme.Dark
          ? css`
              background: ${theme.tertiaryBackgroundColor} !important;

              .ant-table-column-sorter,
              .ant-dropdown-trigger {
                color: ${theme.tableHeaderButtonColor};

                &.active {
                  color: ${theme.teal};
                }
              }

              &:hover,
              &:focus,
              &:active {
                background: ${theme.primaryBackgroundColor} !important;

                .ant-dropdown-trigger {
                  background: ${theme.primaryBackgroundColor} !important;

                  &:hover {
                    background: ${theme.secondaryBackgroundColor} !important;
                  }
                }
              }

              .ant-table-filter-trigger-container {
                background: ${theme.tertiaryBackgroundColor} !important;
                transition: background-color 0.2s ease;

                &:hover,
                &.ant-table-filter-trigger-container-open {
                  background: ${theme.secondaryBackgroundColor} !important;

                  .ant-dropdown-trigger {
                    color: ${theme.labelColor};
                  }
                }
              }
            `
          : ''}
    }

    thead > tr > .ant-table-selection-column,
    thead > tr > .ant-table-cell-scrollbar {
      background: ${({ theme }) => theme.tertiaryBackgroundColor};
      box-shadow: none;
    }

    tr.ant-table-placeholder {
      background: ${({ theme }) => theme.secondaryBackgroundColor} !important;

      .ant-empty-description {
        color: ${({ theme }) => theme.disabledTextColor};
      }
    }

    .ant-table-tbody > tr:hover > td {
      background: ${({ theme }) => theme.tertiaryBackgroundColor};
    }

    tr.ant-table-expanded-row > td {
      background: ${({ theme }) => theme.tertiaryBackgroundColor};
    }

    .ant-table-row-selected > td {
      background: ${({ theme }) => theme.tableAccentColor} !important;
    }

    .ant-table-column-sort {
      background: ${({ theme }) => theme.tertiaryBackgroundColor};
    }

    .ant-table-footer {
      background: ${({ theme }) => theme.secondaryBackgroundColor} !important;
    }

    ${({ theme }) =>
      theme.themeName === Theme.Dark
        ? css`
            .ant-table-thead > tr > th,
            .ant-table-thead > tr > td,
            .ant-table-tbody > tr > td {
              border-bottom: 1px solid ${theme.borderColor}8a;
            }
          `
        : ''}
  }

  .ant-table-row-expand-icon,
  .ant-pagination-item-link,
  .ant-pagination-item,
  .ant-pagination-item > a,
  .ant-pagination-options-size-changer,
  .ant-pagination-options-size-changer > .ant-select-selector {
    background: ${({ theme }) => theme.buttonBackgroundColor};
    border-color: ${({ theme }) => theme.buttonBorderColor};
    color: ${({ theme }) => theme.headerColor};

    &:hover,
    &.ant-pagination-item-active,
    &.ant-pagination-item-active > a {
      border-color: ${({ theme }) => theme.teal};
      color: ${({ theme }) => theme.teal};
    }
  }

  .ant-pagination-options-size-changer > .ant-select-arrow {
    color: ${({ theme }) => theme.headerColor};
  }

  .ant-pagination-disabled > .ant-pagination-item-link {
    background: ${({ theme }) => theme.disabledBackground};
    border-color: ${({ theme }) => theme.disabledTextColor};
    color: ${({ theme }) => theme.disabledTextColor};
  }

  .ant-pagination-item-ellipsis,
  .ant-pagination-total-text {
    color: ${({ theme }) => theme.headerColor} !important;
  }
`;

export const StyledTableV5 = styled(TableV5)<{ $draggingTable?: boolean }>`
  ${ArchivedRowStyle}
  ${MarkedTableRowStyle}
  ${SelectedRowTransitionStyle}

  background: ${({ theme }) => theme.secondaryBackgroundColor};

  .ant-table {
    background: ${({ theme }) => theme.secondaryBackgroundColor};
    color: ${({ theme }) => theme.headerColor};

    ${({ theme }) =>
      theme.themeName === Theme.Dark
        ? css`
            scrollbar-color: unset;
          `
        : ''}

    .ant-table-header {
      border-radius: 0;
    }

    th,
    thead > tr > td.ant-table-row-expand-icon-cell {
      color: ${({ theme }) => theme.labelColor} !important;

      .ant-table-filter-trigger-container-open {
        background: transparent !important;
      }

      ${({ theme }) =>
        theme.themeName === Theme.Dark
          ? css`
              background: ${theme.tertiaryBackgroundColor} !important;

              &:hover,
              &:focus,
              &:active {
                background: ${theme.secondaryBackgroundColor} !important;

                .ant-dropdown-trigger {
                  background: ${theme.secondaryBackgroundColor} !important;
                }
              }

              .ant-table-filter-trigger {
                &.active:has(.ant-table-filter-trigger-container-open) {
                  .anticon {
                    color: ${theme.teal} !important;
                  }
                }
              }
            `
          : ''}
    }

    thead > tr > *:first-child {
      border-start-start-radius: unset !important;
    }
    thead > tr > *:last-child {
      border-start-end-radius: unset !important;
    }

    thead > tr > .ant-table-selection-column,
    thead > tr > .ant-table-cell-scrollbar {
      background: ${({ theme }) => theme.tertiaryBackgroundColor};
      box-shadow: none;
    }

    .ant-table-column-title {
      color: ${({ theme }) => theme.labelColor};
    }

    th.ant-table-cell {
      height: 58px;
      padding: 0 16px;

      .ant-table-column-sorter,
      .ant-dropdown-trigger {
        color: ${({ theme }) => theme.tableHeaderButtonColor};

        &.active {
          color: ${({ theme }) => theme.teal};
        }
      }

      .ant-dropdown-trigger {
        border-radius: unset;
        height: 56px;

        &.ant-dropdown-open,
        &:hover {
          background: ${({ theme }) =>
            theme.themeName === Theme.Dark
              ? theme.secondaryBackgroundColor
              : theme.tableAccentColor} !important;
          color: ${({ theme }) => theme.labelColor};
        }
      }
    }

    th::before {
      display: none;
    }

    tr.ant-table-placeholder {
      background: ${({ theme }) => theme.secondaryBackgroundColor} !important;

      .ant-empty-description {
        color: ${({ theme }) => theme.disabledTextColor};
      }
    }

    .ant-table-tbody > tr:hover > td {
      background: ${({ theme }) => theme.tertiaryBackgroundColor};
    }

    tr.ant-table-expanded-row > td {
      background: ${({ theme }) => theme.tertiaryBackgroundColor};
    }

    .ant-table-row-selected > td {
      background: ${({ theme }) => theme.tableAccentColor} !important;
    }

    .ant-table-column-sort {
      background: ${({ theme }) => theme.tertiaryBackgroundColor};
    }

    .ant-table-filter-dropdown {
      color: ${({ theme }) => theme.headerColor};
    }

    .ant-table-footer {
      background: ${({ theme }) => theme.secondaryBackgroundColor} !important;
    }

    /*
     * Pinned cells stay opaque so scrolled cells pass behind them, but antd
     * paints them the light theme's white. Archived and marked rows are
     * excluded because they set their own cell background.
     */
    .ant-table-tbody > tr:not(.archived):not(.marked-table-row) > td.ant-table-cell-fix-left,
    .ant-table-tbody > tr:not(.archived):not(.marked-table-row) > td.ant-table-cell-fix-right {
      background: ${({ theme }) => theme.secondaryBackgroundColor};
    }
    .ant-table-tbody > tr:not(.archived):not(.marked-table-row):hover > td.ant-table-cell-fix-left,
    .ant-table-tbody
      > tr:not(.archived):not(.marked-table-row):hover
      > td.ant-table-cell-fix-right {
      background: ${({ theme }) => theme.tertiaryBackgroundColor};
    }

    ${({ theme }) =>
      theme.themeName === Theme.Dark
        ? css`
            .ant-table-thead > tr > th,
            .ant-table-thead > tr > td,
            .ant-table-tbody > tr > td {
              border-bottom: 1px solid ${theme.borderColor}8a;
            }
          `
        : ''}
  }

  .ant-table-row-expand-icon,
  .ant-pagination-item,
  .ant-pagination-item > a,
  .ant-pagination-options-size-changer,
  .ant-pagination-options-size-changer > .ant-select-selector {
    background: ${({ theme }) => theme.buttonBackgroundColor};
    border-radius: 2px !important;
    color: ${({ theme }) => theme.headerColor};

    &:hover,
    &.ant-pagination-item-active,
    &.ant-pagination-item-active > a {
      border-color: ${({ theme }) => theme.teal};
      color: ${({ theme }) => theme.teal};
    }
  }

  .ant-pagination-item {
    border: 1px solid ${({ theme }) => theme.buttonBorderColor};
  }

  .ant-pagination-item-link {
    background: ${({ theme }) => theme.buttonBackgroundColor} !important;
    border: 1px solid ${({ theme }) => theme.buttonBorderColor} !important;
    border-radius: 2px !important;
    color: ${({ theme }) => theme.headerColor} !important;

    &:hover {
      border-color: ${({ theme }) => theme.teal} !important;
      color: ${({ theme }) => theme.teal} !important;
    }
  }

  .ant-pagination-disabled > .ant-pagination-item-link {
    background: ${({ theme }) => theme.disabledBackground} !important;
    border-color: ${({ theme }) => theme.disabledTextColor} !important;
    color: ${({ theme }) => theme.disabledTextColor} !important;
  }

  .ant-pagination-options-size-changer > .ant-select-arrow {
    color: ${({ theme }) => theme.headerColor};
  }

  .ant-pagination-item-ellipsis,
  .ant-pagination-total-text {
    color: ${({ theme }) => theme.headerColor} !important;
  }

  thead {
    th.ant-table-cell {
      font-weight: unset;
    }
    .ant-table-filter-column {
      margin: unset;
    }
    .ant-table-column-sorters {
      padding: unset;
    }
  }

  ${({ $draggingTable, theme }) =>
    $draggingTable &&
    `
    box-shadow: 0px 0px 7px 0px ${theme.darkShadow};
    opacity: 0.9;

    thead {
      visibility: collapse;
    }

    .ant-table-row {
      cursor: grabbing !important;
    }
  `}
`;
